import React from 'react';
import { useLocation } from 'react-router-dom';
import Plot from 'react-plotly.js';
import { Reorder } from 'framer-motion';
import { ReactComponent as AibexLogo } from '../../assets/icons/ai-bex-logo.svg';
// Styles for printing
const printStyles = `
  @media print {
    @page {
      size: A4 landscape;
      margin: 0;
    }
    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      background: #fff !important;
    }
    .no-print {
      display: none !important;
    }
    .slide-page {
      box-shadow: none !important;
      break-after: always;
      page-break-after: always;
      width: 100vw !important;
      height: 100vh !important;
      margin: 0 !important;
      border-radius: 0 !important;
    }
  }
`;

const ReportPreview: React.FC = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const type = query.get('type') || 'analysis';
  const ids = query.get('ids')?.split(',') || [];

  // Retrieve Settings from URL
  const companyName = query.get('company') || 'Aibex';
  const clientName = query.get('client') || '株式会社Inovis';

  const today = new Date();
  const formattedDate = `${today.getFullYear()}年${today.getMonth() + 1}月${today.getDate()}日`;

  const slideCommonStyle: React.CSSProperties = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '30px 50px 50px',
    color: '#333',
    position: 'relative'
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
    borderBottom: '2px solid #ccc', // Changed to gray
    paddingBottom: '10px'
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#000',
    margin: 0
  };

  const footerStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: '20px',
    right: '50px',
    fontSize: '11px',
    color: '#aaa'
  };

  // AI Comment Box Component
  const AiAnalysisBox: React.FC<{ text: string }> = ({ text }) => (
    <div style={{ marginTop: '5px', background: '#f9f9f9', padding: '12px 20px', borderRadius: '8px', display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
      <div style={{ flexShrink: 0, marginTop: '2px' }}>
        <AibexLogo width={24} height={24} />
      </div>
      <div style={{ fontSize: '13px', lineHeight: '1.6', color: '#333' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#555', fontSize: '11px' }}>AI ANALYSIS</div>
        {text}
      </div>
    </div>
  );

  // Dummy Slide Renderers
  const renderSlideContent = (id: string, index: number) => {

    // 1. Cover Slide (a1 or p0)
    if (id === 'a1' || id === 'p0') {
      return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', padding: '60px 80px', color: '#000' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '120px' }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#555' }}>{clientName} 御中</div>
            <div style={{ fontSize: '14px', border: '1px solid #000', padding: '4px 12px', fontWeight: 'bold' }}>Confidential</div>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ fontSize: '64px', fontWeight: 'bold', lineHeight: 1.2, marginBottom: '24px', letterSpacing: '-0.02em' }}>
              2025年12月度<br />
              <span style={{ color: type === 'analysis' ? '#0066ff' : '#d455d4' }}>{type === 'analysis' ? '広告運用レポート' : '改善提案レポート'}</span>
            </div>
            <div style={{ fontSize: '24px', color: '#555', borderLeft: `6px solid ${type === 'analysis' ? '#0066ff' : '#d455d4'}`, paddingLeft: '20px', marginTop: '20px' }}>
              案件名： ジェイグラン西明石
            </div>
          </div>
          <div style={{ marginTop: 'auto', borderTop: '2px solid #000', paddingTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#555', marginBottom: '4px' }}>作成日</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{formattedDate}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '24px', fontWeight: '900', color: '#000', letterSpacing: '0.05em' }}>{companyName}</div>
            </div>
          </div>
        </div>
      );
    }

    // 2. Site Monthly Summary (Table & Evaluation) - a2
    if (id === 'a2') {
      const summaryData = [
        { period: '2025/11 (前月)', session: 4800, eSession: 3500, eRate: '72.9%', pvPerSession: 3.2, cv: 65, cvr: '1.35%', req: 40, reqRate: '0.83%', reserve: 25, reserveRate: '0.52%' },
        { period: '2025/10 (前々月)', session: 4500, eSession: 3150, eRate: '70.0%', pvPerSession: 3.0, cv: 58, cvr: '1.28%', req: 35, reqRate: '0.77%', reserve: 23, reserveRate: '0.51%' },
        { period: '2025/09 (3ヶ月前)', session: 4100, eSession: 2800, eRate: '68.2%', pvPerSession: 2.9, cv: 49, cvr: '1.19%', req: 30, reqRate: '0.73%', reserve: 19, reserveRate: '0.46%' },
      ];

      // Calculate Diff
      const current = summaryData[0];
      const prev = summaryData[1];
      const diff = {
        period: '前月差',
        session: current.session - prev.session,
        eSession: current.eSession - prev.eSession,
        eRate: (parseFloat(current.eRate) - parseFloat(prev.eRate)).toFixed(1) + 'pt',
        pvPerSession: (current.pvPerSession - prev.pvPerSession).toFixed(1),
        cv: current.cv - prev.cv,
        cvr: (parseFloat(current.cvr) - parseFloat(prev.cvr)).toFixed(2) + 'pt',
        req: current.req - prev.req,
        reqRate: (parseFloat(current.reqRate) - parseFloat(prev.reqRate)).toFixed(2) + 'pt',
        reserve: current.reserve - prev.reserve,
        reserveRate: (parseFloat(current.reserveRate) - parseFloat(prev.reserveRate)).toFixed(2) + 'pt',
      };

      const renderDiffVal = (val: number | string, isRate = false) => {
        const num = typeof val === 'string' ? parseFloat(val) : val;
        const isPositive = num > 0;
        const color = isPositive ? '#00cc66' : '#ff0000';
        const prefix = isPositive ? '+' : '';
        return <span style={{ color, fontWeight: 'bold' }}>{prefix}{val}</span>;
      };

      return (
        <div style={slideCommonStyle}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>サイト月次推移サマリ</h2>
            <div style={{ fontSize: '12px', color: '#888' }}>2025年12月度</div>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <h3 style={{ fontSize: '14px', marginBottom: '8px', fontWeight: 'bold' }}>月次データ比較</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', textAlign: 'center' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5', borderBottom: '1px solid #ccc' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>対象月</th>
                    <th style={{ padding: '8px' }}>セッション</th>
                    <th style={{ padding: '8px' }}>Eセッション</th>
                    <th style={{ padding: '8px' }}>E率</th>
                    <th style={{ padding: '8px' }}>平均PV</th>
                    <th style={{ padding: '8px' }}>CV</th>
                    <th style={{ padding: '8px' }}>CVR</th>
                    <th style={{ padding: '8px' }}>資料請求</th>
                    <th style={{ padding: '8px' }}>請求率</th>
                    <th style={{ padding: '8px' }}>来場予約</th>
                    <th style={{ padding: '8px' }}>予約率</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ fontWeight: 'bold', background: '#f9f9f9', borderBottom: '2px solid #ddd' }}>
                    <td style={{ padding: '8px', textAlign: 'left' }}>{diff.period}</td>
                    <td style={{ padding: '8px' }}>{renderDiffVal(diff.session)}</td>
                    <td style={{ padding: '8px' }}>{renderDiffVal(diff.eSession)}</td>
                    <td style={{ padding: '8px' }}>{renderDiffVal(diff.eRate)}</td>
                    <td style={{ padding: '8px' }}>{renderDiffVal(diff.pvPerSession)}</td>
                    <td style={{ padding: '8px' }}>{renderDiffVal(diff.cv)}</td>
                    <td style={{ padding: '8px' }}>{renderDiffVal(diff.cvr)}</td>
                    <td style={{ padding: '8px' }}>{renderDiffVal(diff.req)}</td>
                    <td style={{ padding: '8px' }}>{renderDiffVal(diff.reqRate)}</td>
                    <td style={{ padding: '8px' }}>{renderDiffVal(diff.reserve)}</td>
                    <td style={{ padding: '8px' }}>{renderDiffVal(diff.reserveRate)}</td>
                  </tr>
                  {summaryData.map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '8px', textAlign: 'left' }}>{row.period}</td>
                      <td style={{ padding: '8px' }}>{row.session.toLocaleString()}</td>
                      <td style={{ padding: '8px' }}>{row.eSession.toLocaleString()}</td>
                      <td style={{ padding: '8px' }}>{row.eRate}</td>
                      <td style={{ padding: '8px' }}>{row.pvPerSession}</td>
                      <td style={{ padding: '8px' }}>{row.cv}</td>
                      <td style={{ padding: '8px' }}>{row.cvr}</td>
                      <td style={{ padding: '8px' }}>{row.req}</td>
                      <td style={{ padding: '8px' }}>{row.reqRate}</td>
                      <td style={{ padding: '8px' }}>{row.reserve}</td>
                      <td style={{ padding: '8px' }}>{row.reserveRate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <h3 style={{ fontSize: '14px', marginBottom: '8px', fontWeight: 'bold' }}>サイト評価</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <tbody>
                  {[
                    { label: 'セッション', rank: '◎', color: '#00cc66', comment: '季節要因を含めても過去最高水準で推移しており、集客施策が奏功しています。' },
                    { label: 'エンゲージメント', rank: '◯', color: '#ffaa00', comment: '平均PV数は微増。滞在時間は横ばいですが、直帰率は改善傾向にあります。' },
                    { label: 'コンバージョン', rank: '◎', color: '#00cc66', comment: 'EFOの効果によりCVRが大幅に向上。特に「来場予約」の比率が高まっています。' },
                  ].map((item, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '10px', fontWeight: 'bold', width: '150px' }}>{item.label}</td>
                      <td style={{ padding: '10px', textAlign: 'center', fontSize: '20px', fontWeight: 'bold', color: item.color, width: '60px' }}>{item.rank}</td>
                      <td style={{ padding: '10px', color: '#555' }}>{item.comment}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <AiAnalysisBox text="全体として非常に良好な結果です。12月は年末商戦の影響もありセッション数・CV数ともに最高値を記録しました。特にCVRの改善（+0.12pt）は利益率向上に直結する重要な成果です。" />
          <div style={footerStyle}>Page {index + 1}</div>
        </div>
      );
    }

    // 3. Traffic Source Performance (Detailed Table) - a3
    if (id === 'a3') {
      return (
        <div style={slideCommonStyle}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>参照元別成果</h2>
            <div style={{ fontSize: '12px', color: '#888' }}>Traffic Source Details</div>
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', whiteSpace: 'nowrap' }}>
              <thead>
                <tr style={{ background: '#f0f0f0', borderBottom: '2px solid #888' }}>
                  <th style={{ padding: '8px', textAlign: 'left' }}>参照元/メディア</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>ユーザー</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>セッション</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>Eセッション</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>平均E時間</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>E率</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>CV</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>CVR</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>資料請求</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>来場予約</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { name: 'google / cpc', users: 15600, sess: 18500, eSess: 10500, time: '2:15', rate: '56.7%', cv: 85, cvr: '0.46%', req: 50, res: 35 },
                  { name: 'google / p-max', users: 8200, sess: 9800, eSess: 6200, time: '1:45', rate: '63.2%', cv: 62, cvr: '0.63%', req: 40, res: 22 },
                  { name: 'yahoo / cpc', users: 6500, sess: 7200, eSess: 3800, time: '1:30', rate: '52.8%', cv: 24, cvr: '0.33%', req: 14, res: 10 },
                  { name: 'google / organic', users: 5400, sess: 6100, eSess: 4200, time: '3:20', rate: '68.9%', cv: 30, cvr: '0.49%', req: 18, res: 12 },
                  { name: 'facebook / display', users: 4800, sess: 5200, eSess: 2100, time: '0:45', rate: '40.4%', cv: 12, cvr: '0.23%', req: 10, res: 2 },
                  { name: 'instagram / display', users: 3200, sess: 3500, eSess: 1800, time: '0:50', rate: '51.4%', cv: 18, cvr: '0.51%', req: 12, res: 6 },
                  { name: 'direct / none', users: 2100, sess: 2800, eSess: 2400, time: '4:10', rate: '85.7%', cv: 42, cvr: '1.50%', req: 20, res: 22 },
                  { name: 'yahoo / organic', users: 1200, sess: 1400, eSess: 800, time: '2:40', rate: '57.1%', cv: 8, cvr: '0.57%', req: 5, res: 3 },
                ].map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px', fontWeight: 'bold' }}>{row.name}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{row.users.toLocaleString()}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{row.sess.toLocaleString()}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{row.eSess.toLocaleString()}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{row.time}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{row.rate}</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>{row.cv}</td>
                    <td style={{ padding: '8px', textAlign: 'right', color: parseFloat(row.cvr) >= 1.0 ? '#00cc66' : 'inherit' }}>{row.cvr}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{row.req}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{row.res}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <AiAnalysisBox text="Google P-Maxキャンペーン（google / p-max）のE率が63.2%と高く、良質なユーザーを獲得できています。一方でFacebook Display広告は滞在時間が短く、LPとの親和性が低い可能性があります。" />
          <div style={footerStyle}>Page {index + 1}</div>
        </div>
      );
    }

    // a4: Site Page Analysis (Detail) - a4
    if (id === 'a4') {
      const topPages = [
        { p: '/', v: 15200, u: 12000, t: '1:20', e: '55%', s: 'PC:80% SP:65%' },
        { p: '/products/lp-v1', v: 8500, u: 7800, t: '2:40', e: '65%', s: 'PC:90% SP:75%' },
        { p: '/solutions/features', v: 4200, u: 3500, t: '1:50', e: '42%', s: 'PC:60% SP:45%' },
        { p: '/pricing', v: 3800, u: 3100, t: '2:10', e: '72%', s: 'PC:85% SP:70%' },
        { p: '/column/archive', v: 1200, u: 900, t: '3:05', e: '35%', s: 'PC:50% SP:40%' },
        { p: '/company/about', v: 1100, u: 950, t: '1:10', e: '48%', s: 'PC:70% SP:60%' },
        { p: '/access', v: 950, u: 800, t: '0:50', e: '60%', s: 'PC:85% SP:80%' },
        { p: '/contact', v: 900, u: 750, t: '3:30', e: '80%', s: 'PC:95% SP:90%' },
        { p: '/faq', v: 600, u: 500, t: '1:45', e: '50%', s: 'PC:65% SP:55%' },
        { p: '/news/2025-sales', v: 400, u: 350, t: '1:00', e: '30%', s: 'PC:40% SP:30%' },
        { p: '/case-studies', v: 350, u: 300, t: '2:20', e: '58%', s: 'PC:75% SP:60%' },
        { p: '/recruit', v: 300, u: 250, t: '1:30', e: '45%', s: 'PC:60% SP:50%' },
        { p: '/policy', v: 250, u: 200, t: '0:30', e: '20%', s: 'PC:30% SP:25%' },
        { p: '/sitemap', v: 200, u: 180, t: '0:20', e: '15%', s: 'PC:20% SP:15%' },
        { p: '/products/demo', v: 180, u: 150, t: '4:10', e: '75%', s: 'PC:88% SP:72%' },
        { p: '/column/2025-01', v: 150, u: 140, t: '2:50', e: '62%', s: 'PC:70% SP:65%' },
        { p: '/column/2024-12', v: 140, u: 130, t: '2:40', e: '60%', s: 'PC:68% SP:63%' },
        { p: '/features/ai', v: 120, u: 110, t: '1:45', e: '52%', s: 'PC:60% SP:55%' },
        { p: '/features/analytics', v: 110, u: 100, t: '1:40', e: '50%', s: 'PC:58% SP:52%' },
        { p: '/login', v: 100, u: 90, t: '0:40', e: '85%', s: 'PC:90% SP:85%' },
      ];

      return (
        <div style={slideCommonStyle}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>サイトページ分析</h2>
            <div style={{ fontSize: '12px', color: '#888' }}>Pages & Audience</div>
          </div>
          <div style={{ flex: 1, display: 'flex', gap: '20px' }}>

            {/* Left: Page Ranking (Top 20) */}
            <div style={{ flex: 1.8, overflow: 'hidden' }}>
              <h4 style={{ fontSize: '14px', marginBottom: '8px', fontWeight: 'bold' }}>人気ページランキング (Top 20)</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
                    <th style={{ padding: '4px', textAlign: 'left' }}>#</th>
                    <th style={{ padding: '4px', textAlign: 'left' }}>Path</th>
                    <th style={{ padding: '4px', textAlign: 'right' }}>表示回数</th>
                    <th style={{ padding: '4px', textAlign: 'right' }}>ユーザー数</th>
                    <th style={{ padding: '4px', textAlign: 'right' }}>平均滞在</th>
                    <th style={{ padding: '4px', textAlign: 'right' }}>エンゲージ率</th>
                    <th style={{ padding: '4px', textAlign: 'right' }}>スクロール率(PC/SP)</th>
                  </tr>
                </thead>
                <tbody>
                  {topPages.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #eee', background: i < 5 ? '#fafaff' : '#fff' }}>
                      <td style={{ padding: '3px 4px' }}>{i + 1}</td>
                      <td style={{ padding: '3px 4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>{r.p}</td>
                      <td style={{ padding: '3px 4px', textAlign: 'right' }}>{r.v.toLocaleString()}</td>
                      <td style={{ padding: '3px 4px', textAlign: 'right' }}>{r.u.toLocaleString()}</td>
                      <td style={{ padding: '3px 4px', textAlign: 'right' }}>{r.t}</td>
                      <td style={{ padding: '3px 4px', textAlign: 'right' }}>{r.e}</td>
                      <td style={{ padding: '3px 4px', textAlign: 'right', fontSize: '8px' }}>{r.s}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Right: Demographics & Device */}
            <div style={{ flex: 0.8, display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div style={{ display: 'flex', gap: '8px', height: '130px' }}>
                <div style={{ flex: 1, border: '1px solid #eee', borderRadius: '8px', padding: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <h4 style={{ fontSize: '11px', textAlign: 'center', marginBottom: '0px' }}>デバイス</h4>
                  <Plot
                    data={[{ values: [65, 30, 5], labels: ['SP', 'PC', 'Tab'], type: 'pie', hole: 0.6, marker: { colors: ['#333', '#888', '#ccc'] } }]}
                    layout={{ width: 100, height: 100, margin: { t: 0, b: 0, l: 0, r: 0 }, showlegend: false }}
                    config={{ displayModeBar: false }}
                  />
                </div>
                <div style={{ flex: 1, border: '1px solid #eee', borderRadius: '8px', padding: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <h4 style={{ fontSize: '11px', textAlign: 'center', marginBottom: '0px' }}>性別</h4>
                  <Plot
                    data={[{ x: ['男性', '女性'], y: [58, 42], type: 'bar', marker: { color: ['#0066cc', '#cc0066'] } }]}
                    layout={{ width: 100, height: 100, margin: { t: 10, b: 20, l: 20, r: 10 }, yaxis: { range: [0, 80] } }}
                    config={{ displayModeBar: false }}
                  />
                </div>
              </div>
              <div style={{ flex: 1, border: '1px solid #eee', borderRadius: '8px', padding: '12px' }}>
                <h4 style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px' }}>ユーザーセグメント</h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px' }}>
                  <span style={{ color: '#666' }}>主要ターゲット</span>
                  <span style={{ fontWeight: 'bold' }}>30-40代 / 東京</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px' }}>
                  <span style={{ color: '#666' }}>興味・関心</span>
                  <span style={{ fontWeight: 'bold' }}>家族 / 投資</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                  <span style={{ color: '#666' }}>新規 vs 再訪</span>
                  <span style={{ fontWeight: 'bold' }}>新規 65% : 再訪 35%</span>
                </div>
              </div>
            </div>

          </div>
          <AiAnalysisBox text="スマートフォン（SP）比率が65%と高く、30-40代の層が中心です。CV直前ページとして「料金ページ（/pricing）」が15%を占めているため、料金ページからフォームへの導線を強化するとCVR向上が見込めます。" />
          <div style={footerStyle}>Page {index + 1}</div>
        </div>
      );
    }

    // a5: Ad Monthly Summary (Detailed Table) - a5
    if (id === 'a5') {
      const adData = [
        { type: 'Overall', label: '全体合計', imp: 540000, click: 12500, ctr: '2.31%', cpc: 48, cost: 600000, cv: 52, cvr: '0.42%', cpa: 11538 },
        { type: 'Google', label: 'Google 検索', imp: 154000, click: 3200, ctr: '2.08%', cpc: 118, cost: 380000, cv: 42, cvr: '1.31%', cpa: 9047 },
        { type: 'Google', sub: true, label: '　一般', imp: 120000, click: 2000, ctr: '1.67%', cpc: 120, cost: 240000, cv: 12, cvr: '0.60%', cpa: 20000 },
        { type: 'Google', sub: true, label: '　指名', imp: 24000, click: 1000, ctr: '4.17%', cpc: 120, cost: 120000, cv: 28, cvr: '2.80%', cpa: 4285 },
        { type: 'Google', sub: true, label: '　競合', imp: 10000, click: 200, ctr: '2.00%', cpc: 100, cost: 20000, cv: 2, cvr: '1.00%', cpa: 10000 },
        { type: 'Google', sub: true, label: '　P-MAX (広域)', imp: 80000, click: 500, ctr: '0.63%', cpc: 80, cost: 40000, cv: 5, cvr: '1.00%', cpa: 8000 },
        { type: 'Google', sub: true, label: '　P-MAX (中狭域)', imp: 40000, click: 300, ctr: '0.75%', cpc: 90, cost: 27000, cv: 3, cvr: '1.00%', cpa: 9000 },
        { type: 'YSA', label: 'Yahoo! 検索', imp: 85000, click: 1500, ctr: '1.76%', cpc: 120, cost: 180000, cv: 15, cvr: '1.00%', cpa: 12000 },
        { type: 'YSA', sub: true, label: '　一般', imp: 60000, click: 900, ctr: '1.50%', cpc: 133, cost: 120000, cv: 3, cvr: '0.33%', cpa: 40000 },
        { type: 'YSA', sub: true, label: '　指名', imp: 20000, click: 500, ctr: '2.50%', cpc: 100, cost: 50000, cv: 11, cvr: '2.20%', cpa: 4545 },
        { type: 'YSA', sub: true, label: '　競合', imp: 5000, click: 100, ctr: '2.00%', cpc: 100, cost: 10000, cv: 1, cvr: '1.00%', cpa: 10000 },
        { type: 'Meta', label: 'Meta (FB/IG)', imp: 301000, click: 7800, ctr: '2.59%', cpc: 5, cost: 40000, cv: -1, cvr: '-', cpa: -1 },
        { type: 'Meta', sub: true, label: '　広域（認知）', imp: 250000, click: 6000, ctr: '2.40%', cpc: 4, cost: 24000, cv: 0, cvr: '-', cpa: -1 },
        { type: 'Meta', sub: true, label: '　中狭域（リタゲ）', imp: 51000, click: 1800, ctr: '3.53%', cpc: 8, cost: 16000, cv: 0, cvr: '-', cpa: -1 },
      ];

      return (
        <div style={slideCommonStyle}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>広告月次推移サマリ</h2>
            <div style={{ fontSize: '12px', color: '#888' }}>Overall Ad Performance</div>
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#f0f0f0', borderBottom: '2px solid #888' }}>
                  <th style={{ padding: '8px', textAlign: 'left' }}>媒体 / キャンペーン</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>表示回数</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>クリック</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>CTR</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>CPC</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>コスト</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>CV</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>CVR</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>CPA</th>
                </tr>
              </thead>
              <tbody>
                {adData.map((row, i) => (
                  <tr key={i} style={{
                    borderBottom: '1px solid #eee',
                    background: row.type === 'Overall' ? '#e6f7ff' : row.sub ? '#fff' : '#fafafa',
                    fontWeight: row.type === 'Overall' || (!row.sub) ? 'bold' : 'normal'
                  }}>
                    <td style={{ padding: '8px', paddingLeft: row.sub ? '24px' : '8px' }}>{row.label}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{row.imp.toLocaleString()}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{row.click.toLocaleString()}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{row.ctr}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>¥{row.cpc.toLocaleString()}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>¥{row.cost.toLocaleString()}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{row.cv >= 0 ? row.cv : '-'}</td>
                    <td style={{ padding: '8px', textAlign: 'right', color: parseFloat(row.cvr) >= 1.0 ? '#00cc66' : 'inherit' }}>{row.cvr}</td>
                    <td style={{ padding: '8px', textAlign: 'right', color: row.cpa > 15000 ? '#ff0000' : 'inherit' }}>{row.cpa >= 0 ? '¥' + row.cpa.toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <AiAnalysisBox text="Google指名キャンペーンのCPAが¥4,285と非常に優秀です。一方で、Yahoo!一般ワードはCPA ¥40,000と高騰しているため、入札抑制またはKWの見直しが必要です。" />
          <div style={footerStyle}>Page {index + 1}</div>
        </div>
      );
    }

    // a6: Search Ads Performance (Breakdown + Top Keywords)
    if (id === 'a6') {
      const kwListGoogle = [
        { k: 'ジェイグラン西明石', cv: 12, cpa: 3500 },
        { k: '新築マンション 西明石', cv: 8, cpa: 6200 },
        { k: '明石 不動産', cv: 4, cpa: 9800 },
        { k: '明石マンション 3LDK', cv: 3, cpa: 12000 },
        { k: '西明石駅 徒歩3分', cv: 2, cpa: 8500 },
        { k: '明石市 分譲', cv: 2, cpa: 11000 },
        { k: '子育て マンション', cv: 1, cpa: 15000 },
        { k: '明石 新築 4000万', cv: 1, cpa: 22000 },
      ];
      const kwListYahoo = [
        { k: '西明石 マンション', cv: 6, cpa: 4500 },
        { k: '明石市 中古マンション', cv: 2, cpa: 12000 },
        { k: 'ジェイグラン', cv: 3, cpa: 4000 },
        { k: '明石 不動産 投資', cv: 1, cpa: 30000 },
        { k: '西明石 新築', cv: 1, cpa: 8000 },
        { k: '駅近 マンション', cv: 1, cpa: 9500 },
      ];
      const kwListBing = [
        { k: '西明石 賃貸', cv: 1, cpa: 25000 },
        { k: '明石 住みやすさ', cv: 0, cpa: 0 },
        { k: '兵庫県 マンション', cv: 0, cpa: 0 },
      ];

      const renderKwTable = (title: string, data: any[], color: string) => (
        <div style={{ flex: 1, background: '#f9f9f9', borderRadius: '8px', padding: '12px' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', borderBottom: `2px solid ${color}`, color: '#333', fontWeight: 'bold' }}>{title} Top Keywords</h4>
          <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
            <tbody>
              {data.map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '6px 4px' }}>{r.k}</td>
                  <td style={{ padding: '6px 4px', textAlign: 'right' }}>CV:{r.cv}</td>
                  <td style={{ padding: '6px 4px', textAlign: 'right' }}>¥{r.cpa.toLocaleString()}</td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={3} style={{ textAlign: 'center', padding: '15px', color: '#aaa' }}>No Data</td></tr>}
            </tbody>
          </table>
        </div>
      );

      return (
        <div style={slideCommonStyle}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>検索広告実績</h2>
            <div style={{ fontSize: '12px', color: '#888' }}>Search Ads Breakdown</div>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '15px' }}>

            {/* Platform Table */}
            <div style={{ flex: 0.8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#f0f0f0', borderBottom: '2px solid #ddd' }}>
                    <th style={{ padding: '6px', textAlign: 'left' }}>Platform</th>
                    <th style={{ padding: '6px', textAlign: 'right' }}>Imp</th>
                    <th style={{ padding: '6px', textAlign: 'right' }}>Clicks</th>
                    <th style={{ padding: '6px', textAlign: 'right' }}>CTR</th>
                    <th style={{ padding: '6px', textAlign: 'right' }}>Cost</th>
                    <th style={{ padding: '6px', textAlign: 'right' }}>CV</th>
                    <th style={{ padding: '6px', textAlign: 'right' }}>CPA</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { p: 'Google Ads', i: 154000, c: 3200, ctr: '2.08%', cost: 380000, cv: 42, cpa: 9047 },
                    { p: 'Yahoo! Ads', i: 85000, c: 1500, ctr: '1.76%', cost: 180000, cv: 15, cpa: 12000 },
                    { p: 'Bing Ads', i: 12000, c: 180, ctr: '1.50%', cost: 25000, cv: 1, cpa: 25000 },
                  ].map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '6px', fontWeight: 'bold' }}>{row.p}</td>
                      <td style={{ padding: '6px', textAlign: 'right' }}>{row.i.toLocaleString()}</td>
                      <td style={{ padding: '6px', textAlign: 'right' }}>{row.c.toLocaleString()}</td>
                      <td style={{ padding: '6px', textAlign: 'right' }}>{row.ctr}</td>
                      <td style={{ padding: '6px', textAlign: 'right' }}>¥{row.cost.toLocaleString()}</td>
                      <td style={{ padding: '6px', textAlign: 'right' }}>{row.cv}</td>
                      <td style={{ padding: '6px', textAlign: 'right' }}>¥{row.cpa.toLocaleString()}</td>
                    </tr>
                  ))}
                  <tr style={{ background: '#fafafa', fontWeight: 'bold' }}>
                    <td style={{ padding: '6px' }}>Total</td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>251,000</td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>4,880</td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>1.94%</td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>¥585,000</td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>58</td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>¥10,086</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Keyword Split Sections */}
            <div style={{ flex: 1.2, display: 'flex', gap: '15px' }}>
              {renderKwTable('Google', kwListGoogle, '#4285F4')}
              {renderKwTable('Yahoo', kwListYahoo, '#ff3333')}
              {renderKwTable('Bing', kwListBing, '#008080')}
            </div>

          </div>
          <AiAnalysisBox text="Google Adsが獲得の主力であり、CPAも¥9,000台と非常に良好です。Yahoo! AdsはCTRがやや低いため、広告見出しのABテストを実施し、クリック率の向上を図る必要があります。" />
          <div style={footerStyle}>Page {index + 1}</div>
        </div>
      );
    }

    // a7: Area Performance
    if (id === 'a7') {
      return (
        <div style={slideCommonStyle}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>エリア別実績</h2>
            <div style={{ fontSize: '12px', color: '#888' }}>Geographic Performance</div>
          </div>
          <div style={{ flex: 1, display: 'flex', gap: '30px' }}>
            <div style={{ flex: 1, background: '#f5f5f5', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center', color: '#aaa' }}>
                <div>Map Visualization Area</div>
                <div style={{ fontSize: '10px' }}>(Map Library Placeholder)</div>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: '0 0 10px 0' }}>Top Prefectures by CV</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #ddd' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Prefecture</th>
                    <th style={{ padding: '8px', textAlign: 'right' }}>CV</th>
                    <th style={{ padding: '8px', textAlign: 'right' }}>CPA</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { n: 'Tokyo', cv: 45, cpa: 9500 },
                    { n: 'Osaka', cv: 22, cpa: 8200 },
                    { n: 'Kanagawa', cv: 15, cpa: 11000 },
                    { n: 'Hyogo', cv: 12, cpa: 7800 },
                    { n: 'Aichi', cv: 8, cpa: 12500 },
                  ].map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '8px' }}>{row.n}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{row.cv}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>¥{row.cpa.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <AiAnalysisBox text="案件所在地である兵庫県（Hyogo）のCPAが¥7,800と最も安く、地元需要を的確に取り込めています。東京（Tokyo）はボリュームが大きいものの、競合も多いためCPAが上昇傾向にあります。" />
          <div style={footerStyle}>Page {index + 1}</div>
        </div>
      );
    }

    // a8: Display Ads Performance (Cards + Analysis Sidebar)
    if (id === 'a8') {
      const displayCreatives = [
        { name: 'Banner A (Summer)', type: 'Image', ctr: '0.85%', cvr: '1.2%', cpa: 8500, img: '🖼️' },
        { name: 'Video B (Brand)', type: 'Video', ctr: '1.20%', cvr: '0.9%', cpa: 12000, img: '🎬' },
        { name: 'Carousel C (Product)', type: 'Carousel', ctr: '0.95%', cvr: '1.5%', cpa: 9200, img: '🎠' },
        { name: 'Banner D (Discount)', type: 'Image', ctr: '1.10%', cvr: '1.1%', cpa: 9800, img: '🖼️' },
        { name: 'Native E (Article)', type: 'Native', ctr: '0.65%', cvr: '0.8%', cpa: 15000, img: '📰' },
        { name: 'Video F (Shorts)', type: 'Video', ctr: '2.50%', cvr: '0.5%', cpa: 18000, img: '📱' },
        { name: 'Banner G (Retarget)', type: 'Image', ctr: '1.50%', cvr: '1.8%', cpa: 7500, img: '🔄' }, // New
        { name: 'Banner H (New Year)', type: 'Image', ctr: '0.90%', cvr: '1.0%', cpa: 11000, img: '🎍' }, // New
      ];

      return (
        <div style={slideCommonStyle}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>ディスプレイ広告実績</h2>
            <div style={{ fontSize: '12px', color: '#888' }}>Creative Performance</div>
          </div>
          <div style={{ flex: 1, display: 'flex', gap: '20px' }}>

            {/* Left: Creative Grid (Smaller squares) */}
            <div style={{ flex: 2.2, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gridTemplateRows: 'repeat(2, 1fr)', gap: '10px' }}>
              {displayCreatives.map((item, i) => (
                <div key={i} style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '8px', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ aspectRatio: '1/1', background: '#f5f5f5', marginBottom: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', borderRadius: '4px' }}>
                    {item.img}
                  </div>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</h4>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#555' }}>
                    <span>CTR: <b>{item.ctr}</b></span>
                    <span>CVR: <b>{item.cvr}</b></span>
                  </div>
                  <div style={{ marginTop: '2px', fontSize: '10px', textAlign: 'right' }}>
                    CPA: <b style={{ color: item.cpa > 10000 ? '#ff0000' : '#00cc66' }}>¥{item.cpa.toLocaleString()}</b>
                  </div>
                </div>
              ))}
            </div>

            {/* Right: Winning Elements Analysis */}
            <div style={{ flex: 0.8, display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div style={{ flex: 1, background: '#fcfcfc', border: '1px solid #eee', borderRadius: '8px', padding: '15px' }}>
                <h4 style={{ fontSize: '13px', borderBottom: '1px solid #eee', paddingBottom: '5px', marginBottom: '10px' }}>クリエイティブの勝ち筋</h4>

                <div style={{ marginBottom: '15px' }}>
                  <h5 style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>🏆 成果の高い色</h5>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <div style={{ flex: 1, height: '30px', background: '#ff6b6b', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}></div>
                    <div style={{ flex: 1, height: '30px', background: '#4ecdc4', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}></div>
                    <div style={{ flex: 1, height: '30px', background: '#ffe66d', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}></div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#888', marginTop: '4px' }}>
                    <span>Red(Sale)</span><span>Teal(Trust)</span><span>Yel(Attn)</span>
                  </div>
                </div>

                <div>
                  <h5 style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>✍️ 成果の高いキャッチコピー</h5>
                  <ul style={{ fontSize: '11px', paddingLeft: '20px', margin: 0, color: '#444' }}>
                    <li style={{ marginBottom: '4px' }}>「期間・数量限定」の緊急性訴求</li>
                    <li style={{ marginBottom: '4px' }}>具体的な数字（No.1, 98%など）</li>
                    <li>疑問形（〜でお悩みですか？）</li>
                  </ul>
                </div>
              </div>
            </div>

          </div>
          <AiAnalysisBox text="右記の通り、暖色系（赤・黄色）を使用したバナーのCTRが平均1.2倍高い傾向にあります。次回のクリエイティブ制作では、背景色を赤ベースにし、「限定10名」といった具体的数字を大きく配置するABテストを推奨します。" />
          <div style={footerStyle}>Page {index + 1}</div>
        </div>
      );
    }

    // a9: Ad Copy Analysis (Renamed from Keyword Analysis)
    if (id === 'a9') {
      return (
        <div style={slideCommonStyle}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>広告文分析</h2>
            <div style={{ fontSize: '12px', color: '#888' }}>Top Ad Headlines</div>
          </div>
          <div style={{ flex: 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#f0f0f0', borderBottom: '2px solid #ddd' }}>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Headline / Description</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Clicks</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>CTR</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>CV</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>CVR</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>CPA</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { h: 'ジェイグラン西明石 | 駅徒歩3分の新築マンション', d: '全邸南向き、3LDK中心。モデルルーム公開中。', c: 1200, ctr: '2.5%', cv: 30, cvr: '2.50%', cpa: 6000 },
                  { h: '明石エリアで住まい探し | 充実の共用施設', d: 'キッズルーム、ワークラウンジ完備。資料請求はこちら。', c: 850, ctr: '1.8%', cv: 12, cvr: '1.41%', cpa: 9500 },
                  { h: '【公式】ジェイグラン西明石 | 来場予約受付中', d: 'Web予約でQUOカードプレゼント。', c: 600, ctr: '3.2%', cv: 25, cvr: '4.17%', cpa: 4500 },
                  { h: '西明石の新築分譲 | 3LDK 3000万円台〜', d: 'ローン相談会実施中。', c: 450, ctr: '1.5%', cv: 5, cvr: '1.11%', cpa: 12000 },
                  { h: '家賃並みで叶うマイホーム', d: '月々8万円台からの支払いプランをご提案。', c: 300, ctr: '0.9%', cv: 1, cvr: '0.33%', cpa: 25000 },
                ].map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '10px' }}>
                      <div style={{ fontWeight: 'bold', color: '#0066cc', marginBottom: '4px' }}>{row.h}</div>
                      <div style={{ fontSize: '10px', color: '#666' }}>{row.d}</div>
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{row.c.toLocaleString()}</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{row.ctr}</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{row.cv}</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{row.cvr}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: row.cpa > 15000 ? '#ff0000' : 'inherit' }}>¥{row.cpa.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <AiAnalysisBox text="「来場予約」や「QUOカード」などの具体的メリットを提示した広告文（3行目）がCTR 3.2%、CVR 4.17%と非常に高い成果を上げています。一方で「家賃並み」訴求（5行目）はクリックされるもののCVに至っていないため、ターゲット層の見直しが必要です。" />
          <div style={footerStyle}>Page {index + 1}</div>
        </div>
      );
    }

    // a10: P-MAX / Youtube Performance (Refined)
    if (id === 'a10') {
      return (
        <div style={slideCommonStyle}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>P-MAX / Youtube配信状況</h2>
            <div style={{ fontSize: '12px', color: '#888' }}>Automated & Video Ads</div>
          </div>
          <div style={{ flex: 1, display: 'flex', gap: '20px' }}>

            {/* Left: P-MAX */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <h4 style={{ fontSize: '14px', marginBottom: '10px' }}>P-MAX Asset Performance</h4>

              {/* Chart */}
              <div style={{ flex: 1, marginBottom: '10px' }}>
                <Plot
                  data={[
                    { x: ['Oct', 'Nov', 'Dec'], y: [30, 35, 32], name: 'Shopping', type: 'bar', marker: { color: '#4285F4' } },
                    { x: ['Oct', 'Nov', 'Dec'], y: [20, 25, 28], name: 'Display', type: 'bar', marker: { color: '#FBBC05' } },
                    { x: ['Oct', 'Nov', 'Dec'], y: [15, 12, 18], name: 'Video', type: 'bar', marker: { color: '#EA4335' } },
                    { x: ['Oct', 'Nov', 'Dec'], y: [35, 28, 22], name: 'Search', type: 'bar', marker: { color: '#34A853' } },
                  ]}
                  layout={{ barmode: 'stack', width: 330, height: 180, margin: { t: 0, b: 20, l: 30, r: 0 }, showlegend: true, legend: { orientation: 'h', y: 1.1 } }}
                  config={{ displayModeBar: false }}
                />
              </div>

              {/* Additional P-MAX content: Asset Group Analysis Table */}
              <div style={{ marginBottom: '10px' }}>
                <h5 style={{ fontSize: '11px', color: '#666', borderBottom: '1px solid #ccc', paddingBottom: '2px', marginBottom: '5px' }}>Asset Group Breakdown</h5>
                <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
                  <thead style={{ background: '#f0f0f0' }}>
                    <tr><th style={{ padding: '3px', textAlign: 'left' }}>Group Name</th><th style={{ padding: '3px' }}>Conv. Value</th><th style={{ padding: '3px' }}>ROAS</th></tr>
                  </thead>
                  <tbody>
                    <tr><td style={{ padding: '3px' }}>Standard Listing</td><td style={{ padding: '3px', textAlign: 'right' }}>¥420,000</td><td style={{ padding: '3px', textAlign: 'right', fontWeight: 'bold' }}>420%</td></tr>
                    <tr><td style={{ padding: '3px' }}>Holiday Special</td><td style={{ padding: '3px', textAlign: 'right' }}>¥280,000</td><td style={{ padding: '3px', textAlign: 'right', fontWeight: 'bold' }}>350%</td></tr>
                    <tr><td style={{ padding: '3px' }}>Dynamic Remarketing</td><td style={{ padding: '3px', textAlign: 'right' }}>¥150,000</td><td style={{ padding: '3px', textAlign: 'right', fontWeight: 'bold' }}>280%</td></tr>
                  </tbody>
                </table>
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '11px', background: '#f5f5f5', padding: '8px', borderRadius: '6px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#888' }}>Cost</div>
                  <div style={{ fontWeight: 'bold' }}>¥150,000</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#888' }}>CV</div>
                  <div style={{ fontWeight: 'bold' }}>18</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#888' }}>CPA</div>
                  <div style={{ fontWeight: 'bold', color: '#00cc66' }}>¥8,333</div>
                </div>
              </div>
            </div>

            {/* Right: Youtube */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <h4 style={{ fontSize: '14px', marginBottom: '10px' }}>Youtube 動画広告</h4>
              <div style={{ flex: 1 }}>
                <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #ddd', background: '#fa111105' }}>
                      <th style={{ textAlign: 'left', padding: '6px' }}>Video (Thumb)</th>
                      <th style={{ textAlign: 'right', padding: '6px' }}>Views</th>
                      <th style={{ textAlign: 'right', padding: '6px' }}>VTR</th>
                      <th style={{ textAlign: 'right', padding: '6px' }}>CPV</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { v: 'Brand Movie 30s', views: 45000, vtr: '35%', cpv: '¥3', color: '#ffaaaa' },
                      { v: 'Interview 60s', views: 12000, vtr: '42%', cpv: '¥5', color: '#aaffaa' },
                      { v: 'Room Tour 15s', views: 85000, vtr: '28%', cpv: '¥2', color: '#aaaaff' },
                      { v: 'Customer Voice 90s', views: 5000, vtr: '50%', cpv: '¥8', color: '#ffffaa' },
                      { v: 'Shorts Teaser 10s', views: 22000, vtr: '15%', cpv: '¥1', color: '#ffaaff' },
                    ].map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px 4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '30px', height: '20px', background: r.color, borderRadius: '4px', flexShrink: 0 }}></div>
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '80px' }}>{r.v}</span>
                        </td>
                        <td style={{ padding: '8px 4px', textAlign: 'right' }}>{r.views.toLocaleString()}</td>
                        <td style={{ padding: '8px 4px', textAlign: 'right' }}>{r.vtr}</td>
                        <td style={{ padding: '8px 4px', textAlign: 'right' }}>{r.cpv}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'flex', gap: '8px', fontSize: '11px', background: '#ffebee', padding: '8px', borderRadius: '6px', marginTop: '10px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#888' }}>Views</div>
                  <div style={{ fontWeight: 'bold' }}>169,000</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#888' }}>Clicks</div>
                  <div style={{ fontWeight: 'bold' }}>1,550</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#888' }}>CV (VT)</div>
                  <div style={{ fontWeight: 'bold' }}>7</div>
                </div>
              </div>
            </div>

          </div>
          <AiAnalysisBox text="アセットグループ別では「Holiday Special」のROASが350%と突出しており、季節性クリエイティブの効果が可視化されました。Youtubeは「Room Tour」が最も安価(¥2)で再生されており、認知獲得の柱となっています。" />
          <div style={footerStyle}>Page {index + 1}</div>
        </div>
      );
    }

    // Generic Fallback
    return (
      <div style={slideCommonStyle}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>{id === 'a4' ? 'サイトページ分析' : id === 'a6' ? '検索広告実績' : 'Report Slide'}</h2>
        </div>
        <div style={{ flex: 1, background: '#f8f9fa', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed #ddd' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '64px', color: '#ddd', marginBottom: '16px' }}>📊</div>
            <p style={{ color: '#888', fontSize: '18px' }}>Content Placeholder for {id}</p>
            <p style={{ color: '#aaa', fontSize: '14px' }}>Detailed chart data will be implemented here.</p>
          </div>
        </div>
        <div style={footerStyle}>Page {index + 1}</div>
      </div>
    );
  };

  return (
    <div style={{ background: '#111', minHeight: '100vh', padding: '40px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '40px' }}>
      <style>{printStyles}</style>

      {/* Toolbar */}
      <div className="no-print" style={{
        position: 'fixed', top: '20px', right: '20px', zIndex: 1000,
        display: 'flex', gap: '12px', background: 'rgba(0,0,0,0.8)', padding: '8px 16px', borderRadius: '50px', backdropFilter: 'blur(10px)'
      }}>
        <button
          onClick={() => window.print()}
          style={{
            background: '#fff', color: '#000', border: 'none', padding: '10px 24px', borderRadius: '24px', fontWeight: 'bold', cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: '8px'
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9V2h12v7"></path><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
          PDFとして保存
        </button>
      </div>

      {/* Slides */}
      {ids.map((id, index) => (
        <div
          key={id}
          className="slide-page"
          style={{
            width: '297mm', // A4 Landscape width
            height: '210mm', // A4 Landscape height
            background: '#fff',
            boxShadow: '0 0 50px rgba(0,0,0,0.5)',
            position: 'relative',
            overflow: 'hidden',
            padding: 0
          }}
        >
          {renderSlideContent(id, index)}
        </div>
      ))}

      {ids.length === 0 && (
        <div style={{ color: '#fff', marginTop: '100px' }}>No slides selected.</div>
      )}
    </div>
  );
};

export default ReportPreview;
